import React from 'react';
import MenuIcon from '@mui/icons-material/Menu';
import { AppBar, Box, FormControlLabel, IconButton, Switch, Toolbar, Typography } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { Tag } from 'antd';
import packageJson from '../../../package.json';
import useIsMobile from '../../hooks/useIsMobile';
import DrawerPrincipal from '../drawer/drawer-principal';
import TabBarMobile from '../tabbar/tabbar-mobile';
import MobileSidebar from '../sidebar/mobile-sidebar';
import { useMemo } from 'react';
import { useNavigation } from '../../shared/hooks/use-navigation.js';
import { getNewLayoutPreference, setNewLayoutPreference, subscribeToNewLayoutPreference } from '../../shared/utils/layout-preference.js';

/**
 * Componente de layout principal que renderiza Drawer no desktop e TabBar no mobile
 * @param {Object} props - Propriedades do componente
 * @param {string} props.title - Título da página
 * @param {React.ReactNode} props.children - Conteúdo da página
 * @param {boolean} props.showTabBar - Se deve mostrar a TabBar (default: true)
 * @param {boolean} props.forceNewLayout - Força uso do novo layout apenas com TabBar
 */
function LayoutMain({ title, children, showTabBar = true, forceNewLayout = false }) {
  const isMobile = useIsMobile();

  const { navigateWithLoading } = useNavigation()

  const [open, setOpen] = React.useState(false);

  const [useNewLayout, setUseNewLayout] = React.useState(getNewLayoutPreference)

  React.useEffect(() => {
    const handlePreferenceChange = () => {
      setUseNewLayout(getNewLayoutPreference())
    }

    return subscribeToNewLayoutPreference(handlePreferenceChange)
  }, [])

  const usuario = useMemo(() => {
    try {
      const stored = localStorage.getItem("user_token")
      return stored ? JSON.parse(stored) : {}
    } catch (error) {
      console.error("Erro ao ler user_token:", error)
      return {}
    }
  }, [])

  const handleLayoutToggle = (event) => {
    const newValue = event.target.checked
    setUseNewLayout(newValue)
    setNewLayoutPreference(newValue)

    if (newValue) {
      navigateWithLoading('/vendas')
    } else {
      navigateWithLoading("/listSale")
    }
  }

  // const navigate = useNavigate();

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  React.useEffect(() => {
    if (isMobile || forceNewLayout) {
      setOpen(false);
    }
  }, [isMobile, forceNewLayout]);

  // Se estiver usando novo layout forçado ou for mobile, usa AppBar + TabBar
  if (isMobile || forceNewLayout) {
    return (
      <>
        <CssBaseline />
        <AppBar position="fixed" open={open} sx={{ bgcolor: '#000000' }}>
          <Toolbar>
            {/* Mostrar menu apenas se não for novo layout */}
            {!forceNewLayout && (
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerOpen}
                sx={{
                  mr: 2,
                  ...(open && !isMobile && { display: 'none' }),
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
            <Typography variant="h6" noWrap component="div">
              {usuario?.name || 'Usuário'}
            </Typography>
            <Tag color={packageJson.ambiente === 'Homologação' ? 'orange' : '#000'} sx={{ marginLeft: 'auto' }}>
              {packageJson.ambiente}: {packageJson.version}
            </Tag>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  color="info"
                  checked={useNewLayout}
                  onChange={handleLayoutToggle}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#90caf9"
                    },
                    "& .MuiSwitch-switchBase:not(.Mui-checked) + .MuiSwitch-track": {
                      backgroundColor: "#424242"
                    },
                    "& .MuiSwitch-switchBase:not(.Mui-checked) .MuiSwitch-thumb": {
                      backgroundColor: "#9e9e9e"
                    }
                  }}
                />
              }
              label={<Typography variant="body2">Novo Layout</Typography>}
              sx={{
                marginRight: 2,
                color: "#e4e3e3ff",
                "& .MuiFormControlLabel-label": { color: "gray" },
              }}
            />
          </Toolbar>
        </AppBar>

        {/* Mostrar sidebar apenas se não for novo layout */}
        {!forceNewLayout && <MobileSidebar open={open} onClose={handleDrawerClose} />}

        <Box sx={{ pb: showTabBar ? "var(--mobile-tabbar-height)" : 0, pt: 12 }}>
          {children}
        </Box>

        {showTabBar && <TabBarMobile />}
      </>
    );
  }

  // Desktop com novo layout (apenas TabBar)
  if (forceNewLayout) {
    return (
      <>
        <CssBaseline />
        <AppBar position="fixed" sx={{ bgcolor: '#000000' }}>
          <Toolbar>
            <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
              {usuario?.name || 'Usuário'}
            </Typography>
            <Tag color={packageJson.ambiente === 'Homologação' ? 'orange' : '#000'} sx={{ marginLeft: 'auto' }}>
              {packageJson.ambiente}: {packageJson.version}
            </Tag>
          </Toolbar>
        </AppBar>

        <Box sx={{ pb: showTabBar ? "var(--mobile-tabbar-height)" : 0, pt: 12 }}>
          {children}
        </Box>

        {showTabBar && <TabBarMobile />}
      </>
    );
  }

  // Desktop com drawer normal (layout tradicional)
  return (
    <DrawerPrincipal title={title}>
      {children}
    </DrawerPrincipal>
  );
}

export default LayoutMain;
